import React, { useState, useEffect, useMemo } from "react";
import { useParams, useRouteMatch } from "react-router-dom";
import { URL } from "../../utils/constants";
import { getUser } from "../../utils/api";
import styles from "./styles.module.css";
import { FaRegCopy, FaPlus, FaTrash, FaChevronDown } from "react-icons/fa";

const SingleCard = () => {
  const { name } = useParams();
  const { url } = useRouteMatch();

  const [station, setStation] = useState({});
  const [canArchive, setCanArchive] = useState(false);

  const currentArchives = station.archive || [];

  // серверные списки для выбора
  const [listInternal, setListInternal] = useState([]);
  const [listExternal, setListExternal] = useState([]);

  // выбранные элементы (мультивыбор)
  const [selInt, setSelInt] = useState(new Set());
  const [selExt, setSelExt] = useState(new Set());

  // состояния загрузки/ошибок
  const [loadInt, setLoadInt] = useState(false);
  const [loadExt, setLoadExt] = useState(false);
  const [errInt, setErrInt] = useState("");
  const [errExt, setErrExt] = useState("");

  // строки поиска
  const [qInternal, setQInternal] = useState("");
  const [qExternal, setQExternal] = useState("");

  // форма «добавить новый»
  const [form, setForm] = useState({ representation_name: "", archive_link: "" });

  // сворачиваемые блоки
  const [openAttach, setOpenAttach] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);

  const token = localStorage.getItem("auth_token");
  const authHeader = token ? { authorization: `Token ${token}` } : {};

  // загрузка станции
  useEffect(() => {
    fetch(`${URL}/api/stations/${name}/`, { headers: authHeader })
      .then((r) => r.json())
      .then((data) => setStation(data))
      .catch((e) => console.error("GET station error:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, token]);

  // права
  useEffect(() => {
    if (!token) return;
    getUser()
      .then((me) => {
        const ok = (me?.groups || []).includes("archive") || !!me?.is_staff;
        setCanArchive(ok);
      })
      .catch((e) => console.error("GET me error:", e));
  }, [token]);

  // helpers
  const copyTextToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); }
    catch (err) { console.error("Ошибка копирования:", err); }
  };

  const toHref = (raw = "") => {
    const link = String(raw || "").trim();
    if (!link) return "#";
    if (/^https?:\/\//i.test(link)) return link;
    if (/^file:\/\//i.test(link)) return link;
    if (/^[a-zA-Z]:\\/.test(link)) return "file:///" + link.replace(/\\/g, "/");
    if (/^\\\\/.test(link)) { const noLeading = link.replace(/^\\\\/, ""); return "file://" + noLeading.replace(/\\/g, "/"); }
    return link;
  };

  const keyOf = (a) => String(a?.id ?? a?.archive_link ?? "");

  // открепить (удаляет сам объект архива, как и раньше)
  const detachArchive = async (archiveId) => {
    if (!token) return alert("Нужна авторизация.");
    if (!window.confirm("Открепить архив?")) return;
    try {
      const resp = await fetch(`${URL}/api/archives/${archiveId}/`, {
        method: "DELETE",
        headers: { ...authHeader },
      });
      if (resp.status !== 204) {
        const t = await resp.text();
        throw new Error(t || "Ошибка удаления");
      }
      setStation((prev) => ({
        ...prev,
        archive: (prev.archive || []).filter((a) => a.id !== archiveId),
      }));
    } catch (e) {
      console.error(e);
      alert("Не удалось открепить архив");
    }
  };

  // создать новый архив и сразу прикрепить к текущей станции
  const submitNew = async (e) => {
    e?.preventDefault?.();
    if (!token) return alert("Нужна авторизация.");

    const nm = form.representation_name.trim();
    const lk = form.archive_link.trim();
    if (!nm || !lk) return alert("Укажите и название, и ссылку.");

    const payload = {
      representation_name: nm,
      archive_link: lk,
      target_model: "station",
      target_id: Number(name),
    };

    try {
      const resp = await fetch(`${URL}/api/archives/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || "Ошибка создания");
      }
      const created = await resp.json();
      setStation((prev) => ({ ...prev, archive: [created, ...(prev.archive || [])] }));
      setForm({ representation_name: "", archive_link: "" });
    } catch (e) {
      console.error(e);
      alert("Не удалось добавить архив");
    }
  };

  // пакетное прикрепление
  const attachMany = async (items) => {
    if (!token) return alert("Нужна авторизация.");
    if (!items?.length) return;

    try {
      const createdList = await Promise.all(
        items.map((src) =>
          fetch(`${URL}/api/archives/`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeader },
            body: JSON.stringify({
              representation_name: src.representation_name,
              archive_link: src.archive_link,
              target_model: "station",
              target_id: Number(name),
            }),
          }).then(async (r) => {
            if (!r.ok) throw new Error(await r.text());
            return r.json();
          })
        )
      );
      setStation((prev) => ({
        ...prev,
        archive: [...createdList, ...(prev.archive || [])],
      }));
    } catch (e) {
      console.error(e);
      alert("Часть архивов прикрепить не удалось");
    }
  };

  const attachSelected = async (kind) => {
    const list = kind === "internal" ? listInternal : listExternal;
    const sel = kind === "internal" ? selInt : selExt;
    const items = list.filter((a) => sel.has(keyOf(a)));
    await attachMany(items);
    if (kind === "internal") setSelInt(new Set());
    else setSelExt(new Set());
  };

  // классификация уже прикреплённых ссылок
  const grouped = useMemo(() => {
    const g = { tDrive: [], pDrive: [], others: [] };
    for (const el of currentArchives) {
      const l = el?.archive_link || "";
      if (/^T:/i.test(l)) g.tDrive.push(el);
      else if (/^P:/i.test(l)) g.pDrive.push(el);
      else g.others.push(el);
    }
    return g;
  }, [currentArchives]);

  // серверные загрузчики списков с дебаунсом
  useEffect(() => {
    if (!canArchive) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      setLoadInt(true);
      setErrInt("");
      const q = qInternal ? `&q=${encodeURIComponent(qInternal)}` : "";
      fetch(`${URL}/api/archives/?kind=internal${q}`, {
        headers: authHeader,
        signal: ctrl.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          const arr = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
          setListInternal(arr);
        })
        .catch((e) => {
          if (e.name !== "AbortError") setErrInt("Не удалось загрузить внутренние ссылки");
          console.error(e);
        })
        .finally(() => setLoadInt(false));
    }, 250);
    return () => { ctrl.abort(); clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canArchive, token, qInternal]);

  useEffect(() => {
    if (!canArchive) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      setLoadExt(true);
      setErrExt("");
      const q = qExternal ? `&q=${encodeURIComponent(qExternal)}` : "";
      fetch(`${URL}/api/archives/?kind=external${q}`, {
        headers: authHeader,
        signal: ctrl.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          const arr = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
          setListExternal(arr);
        })
        .catch((e) => {
          if (e.name !== "AbortError") setErrExt("Не удалось загрузить внешние ссылки");
          console.error(e);
        })
        .finally(() => setLoadExt(false));
    }, 250);
    return () => { ctrl.abort(); clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canArchive, token, qExternal]);

  // рендер уже прикреплённых ссылок
  const renderLinks = (links) =>
    links.map((el) => {
      const href = toHref(el.archive_link);
      return (
        <div key={`${el.id}-${el.archive_link}`} className={styles.linkRow}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
            title={el.archive_link}
          >
            {el.representation_name}
          </a>
          <button
            type="button"
            className={styles.iconBtn}
            title="Копировать ссылку"
            onClick={() => copyTextToClipboard(el.archive_link)}
          >
            <FaRegCopy />
          </button>
          {canArchive && (
            <button
              type="button"
              className={styles.iconBtnDanger}
              title="Открепить"
              onClick={() => detachArchive(el.id)}
            >
              <FaTrash />
            </button>
          )}
        </div>
      );
    });

  // переключатель выбора в списке
  const toggleSelect = (kind, item) => {
    const k = keyOf(item);
    if (!k) return;
    if (kind === "internal") {
      const next = new Set(selInt);
      next.has(k) ? next.delete(k) : next.add(k);
      setSelInt(next);
    } else {
      const next = new Set(selExt);
      next.has(k) ? next.delete(k) : next.add(k);
      setSelExt(next);
    }
  };

  // общий рендер списков
  const renderPickList = (kind, list, loading, error, q, setQ, selectedSet) => (
    <div className={styles.attachRow}>
      <div className={styles.label}>{kind === "internal" ? "Внутренняя ссылка" : "Внешняя ссылка (удаленный сервер)"}</div>
      <input
        className={styles.searchInput}
        placeholder="Поиск по названию…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <ul className={styles.scrollList} role="listbox" aria-multiselectable="true" tabIndex={0}>
        {loading ? (
          <li className={styles.muted}>Загрузка…</li>
        ) : error ? (
          <li className={styles.error}>{error}</li>
        ) : list.length ? (
          list.map((a) => {
            const id = keyOf(a);
            const selected = selectedSet.has(id);
            return (
              <li
                key={`${kind}-${id}`}
                className={`${styles.listItem} ${selected ? styles.listItemSelected : ""}`}
                onClick={() => toggleSelect(kind, a)}
                aria-selected={selected}
              >
                <span className={styles.listItemText} title={a.representation_name}>
                  {a.representation_name || "(без названия)"}
                </span>
                <span className={styles.checkMark} aria-hidden="true">
                  {selected ? "✓" : ""}
                </span>
              </li>
            );
          })
        ) : (
          <li className={styles.empty}>Ничего не найдено</li>
        )}
      </ul>
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() => attachSelected(kind)}
        disabled={!selectedSet.size}
        title={!selectedSet.size ? "Выберите элементы" : "Прикрепить выбранные"}
      >
        Прикрепить выбранные ({selectedSet.size})
      </button>
    </div>
  );

  return (
    <div className={styles.card}>
      <div className={styles.title}>{station.name}</div>

      {grouped.tDrive.length > 0 && (
        <div className={styles.block}>
          <h3 className={styles.h3}>Ссылки на внутреннем сервере</h3>
          {renderLinks(grouped.tDrive)}
        </div>
      )}
      {grouped.pDrive.length > 0 && (
        <div className={styles.block}>
          <h3 className={styles.h3}>Ссылки на удалённом сервере</h3>
          {renderLinks(grouped.pDrive)}
        </div>
      )}
      {grouped.others.length > 0 && (
        <div className={styles.block}>
          <h3 className={styles.h3}>Другие ссылки</h3>
          {renderLinks(grouped.others)}
        </div>
      )}

      {canArchive && (
        <div className={styles.archivePanel}>
          <button
            type="button"
            className={styles.collapseHeader}
            onClick={() => setOpenAttach((v) => !v)}
            aria-expanded={openAttach}
          >
            <span className={styles.collapseTitle}>Прикрепить из существующих</span>
            <FaChevronDown className={`${styles.chevron} ${openAttach ? styles.chevronOpen : ""}`} />
          </button>

          {openAttach && (
            <div className={styles.collapseBody}>
              {renderPickList("internal", listInternal, loadInt, errInt, qInternal, setQInternal, selInt)}
              {renderPickList("external", listExternal, loadExt, errExt, qExternal, setQExternal, selExt)}
            </div>
          )}

          <button
            type="button"
            className={styles.collapseHeader}
            onClick={() => setOpenAdd((v) => !v)}
            aria-expanded={openAdd}
          >
            <span className={styles.collapseTitle}>
              <FaPlus style={{ marginRight: 6 }} />
              Добавить новый архив
            </span>
            <FaChevronDown className={`${styles.chevron} ${openAdd ? styles.chevronOpen : ""}`} />
          </button>

          {openAdd && (
            <div className={styles.collapseBody}>
              <form onSubmit={submitNew} className={styles.formGrid}>
                <label className={styles.label}>
                  Название
                  <input
                    type="text"
                    className={styles.input}
                    value={form.representation_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, representation_name: e.target.value }))
                    }
                    placeholder="Напр. «Том 4 — ПД»"
                  />
                </label>
                <label className={styles.label}>
                  Ссылка
                  <input
                    type="text"
                    className={styles.input}
                    value={form.archive_link}
                    onChange={(e) => setForm((f) => ({ ...f, archive_link: e.target.value }))}
                    placeholder="T:\\... или P:\\... или https://..."
                  />
                </label>
                <button className={styles.primaryBtnWide} type="submit">
                  Добавить
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {canArchive && (
        <div className={styles.editLink}>
          <a href={`${url}/edit`} className={styles.editA}>
            Редактировать содержимое станции
          </a>
        </div>
      )}
    </div>
  );
};

export default SingleCard;
